/**
 * What: Existing-account sign-in navigation for one verified Shopify shop.
 * Transport / data-flow: Uses the existing bootstrap and authenticated API transport; no Web cookie/account is required.
 * Invariants: Rechecks App Bridge/session scope before requests and navigation; never derives permission from browser shop parameters.
 */
import * as z from 'zod'
import { captureAuthenticatedShopBootstrap } from './authenticatedShopBootstrap'
import { shopifyApiFetch, type ShopifyAppBridge } from './shopifyApiFetch'
import { throwMerchantFacingApiError } from './shopifyApiError'
import { requestShopifyNavigation, type NavigationClient } from './shopifyNavigation'
import { parseContractPayload } from './merchantErrors'
import { getShopifyJourney } from './shopifyJourney'

const connectionIntentSchema = z.object({ connectUrl: z.string().url() })

/** Starts the existing signed-intent handoff; Web rechecks admission at signup and connection writes. */
export async function openShopifyLaunchConnection(
  shopify: ShopifyAppBridge & NavigationClient,
  expectedShop: string,
  signal: AbortSignal,
): Promise<void> {
  const bootstrap = await captureAuthenticatedShopBootstrap(shopify, { expectedShop, signal })
  const journeyId = getShopifyJourney(bootstrap.shopDomain)
  const response = await shopifyApiFetch(
    bootstrap.authenticatedShopify,
    `/api/testfeed-connection-intent?journeyId=${journeyId}`,
    {
      init: { signal },
    },
  )
  if (!response.ok) return throwMerchantFacingApiError(response, 'connect-intent')
  const intent = parseContractPayload('shopify-connection-intent', connectionIntentSchema, await response.json())
  const left = await requestShopifyNavigation(shopify, () => {
    if (signal.aborted || shopify.config?.shop !== bootstrap.shopDomain)
      throw new DOMException('Shopify store scope changed', 'AbortError')
    globalThis.window?.open(intent.connectUrl, '_top', 'noopener')
  })
  if (!left) throw new DOMException('Navigation cancelled', 'AbortError')
}
